import { useMemo } from "react";
import { Filters } from "../models/Filters";
import { Invoice } from "../models/Invoice";

export const useFilteredInvoices = (invoices: Invoice[], filters: Filters) => {
  return useMemo(() => {
    const {
      invoiceDate,
      paymentDate,
      name,
      type,
      yearQuarter,
      selectedDescription,
      sortDirection,
    } = filters;

    let result = [...invoices];

    if (invoiceDate) {
      result = result.filter((inv) =>
        matchesDateFilter(new Date(inv.invoiceDate), filters.invoiceDate)
      );
    }

    if (paymentDate) {
      result = result.filter(
        (inv) =>
          inv.paymentDate &&
          matchesDateFilter(new Date(inv.paymentDate), filters.paymentDate)
      );
    }

    if (name) {
      result = result.filter((inv) =>
        inv.name.toLowerCase().includes(name.toLowerCase())
      );
    }

    if (type) {
      result = result.filter((inv) => inv.type === type);
    }

    if (selectedDescription) {
      result = result.filter(
        (inv) =>
          inv.description.toLowerCase().includes(selectedDescription.toLowerCase())
      );
    }

    if (yearQuarter && yearQuarter.length > 0) {
      result = result.filter((inv) => {
        const date = new Date(inv.invoiceDate);
        const year = date.getFullYear().toString();
        const month = date.getMonth() + 1;
        const quarter =
          month <= 3 ? "Q1" : month <= 6 ? "Q2" : month <= 9 ? "Q3" : "Q4";
        return yearQuarter.includes(`${year} ${quarter}`);
      });
    }

    if (sortDirection?.key) {
      const { key, direction } = sortDirection;

      result.sort((a, b) => {
        let aVal = a[key as keyof Invoice];
        let bVal = b[key as keyof Invoice];

        // Handle known date keys
        if (key === "invoiceDate" || key === "paymentDate") {
          aVal = new Date(aVal as string);
          bVal = new Date(bVal as string);

          return direction === "asc"
            ? (aVal as Date).getTime() - (bVal as Date).getTime()
            : (bVal as Date).getTime() - (aVal as Date).getTime();
        }

        // Handle string values
        if (typeof aVal === "string" && typeof bVal === "string") {
          return direction === "asc"
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }

        // Add number support if needed
        if (typeof aVal === "number" && typeof bVal === "number") {
          return direction === "asc" ? aVal - bVal : bVal - aVal;
        }

        return 0;
      });
    }

    return result;
  }, [invoices, filters]);
};

function matchesDateFilter(date: Date, filterStr: string): boolean {
  const input = filterStr.trim();
  const currentYear = new Date().getFullYear();

  const pad = (n: number) => n.toString().padStart(2, "0");
  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1);
  const year = date.getFullYear().toString();

  if (/^\d{2}$/.test(input)) {
    return month === input;
  }

  if (/^\d{2}\/\d{2}$/.test(input)) {
    const [d, m] = input.split("/");
    return day === d && month === m && year === currentYear.toString();
  }

  if (/^\d{4}$/.test(input)) {
    return year === input;
  }

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(input)) {
    const [d, m, y] = input.split("/");
    return day === d && month === m && year === y;
  }

  return false;
}
